import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { AlertTriangle, RefreshCw, Ban } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";

export default function ProtectionPaymentCancel() {
  const [params] = useSearchParams();
  const contractId = params.get("contract_id");
  const [contract, setContract] = useState(null);
  const [retrying, setRetrying] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [cancelled, setCancelled] = useState(false);

  useEffect(() => {
    if (!contractId) return;
    api.get(`/protection/contract-info/${contractId}`).then((r) => setContract(r.data)).catch(() => {});
  }, [contractId]);

  const retryPayment = async () => {
    if (!contractId) return;
    setRetrying(true);
    try {
      const { data } = await api.post(`/protection/contracts/${contractId}/retry-payment`, { origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Zahlung konnte nicht gestartet werden.");
      setRetrying(false);
    }
  };

  const declineBooking = async () => {
    if (!contractId) return;
    setCancelling(true);
    try {
      await api.post(`/protection/contracts/${contractId}/cancel-unpaid`);
      setCancelled(true);
      toast.success("Buchung storniert.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Stornierung fehlgeschlagen.");
    } finally { setCancelling(false); }
  };

  return (
    <section data-testid="protection-payment-cancel" className="relative z-10 bg-[#050505] py-20 lg:py-28 min-h-[70vh]">
      <div className="max-w-2xl mx-auto px-6 lg:px-10">
        <div className="glass rounded-3xl p-10 lg:p-14">
          <AlertTriangle size={44} className="text-amber-400 mb-6" />
          <h2 className="font-display text-4xl lg:text-5xl">Zahlung abgebrochen.</h2>

          {cancelled ? (
            <>
              <p data-testid="protection-payment-cancel-confirmed" className="text-[#aaa] mt-4 leading-relaxed">
                Deine Schutzpaket-Buchung wurde storniert. Es wurde nichts abgebucht.
              </p>
              <Link to="/" data-testid="protection-payment-cancel-home" className="mt-8 inline-block text-sm text-white underline underline-offset-4 hover:text-[#ccc] transition-colors">
                Zurück zur Startseite
              </Link>
            </>
          ) : (
            <>
              <p className="text-[#aaa] mt-4 leading-relaxed">
                Deine Zahlung wurde nicht abgeschlossen. Deine Angaben
                {contract?.contract_number ? <> <span className="text-white font-mono">{contract.contract_number}</span></> : ""} sind noch gespeichert, das
                Schutzpaket ist aber noch nicht aktiv. Du kannst die Zahlung jetzt erneut versuchen oder die Buchung stornieren.
              </p>

              {contract && (
                <div className="mt-6 rounded-2xl border border-white/10 bg-[#0a0a0a] p-6 text-sm">
                  <div className="flex justify-between"><span className="text-[#888]">Gerät</span><span className="text-white">{contract.device}</span></div>
                  <div className="flex justify-between mt-2"><span className="text-[#888]">Monatsbeitrag</span><span className="font-mono text-white">{contract.monthly_price.toFixed(2)} € / Monat</span></div>
                </div>
              )}

              <div className="mt-8 flex flex-col sm:flex-row gap-3">
                <button onClick={retryPayment} disabled={retrying} data-testid="protection-payment-retry-button"
                  className="flex-1 inline-flex items-center justify-center gap-2 rounded-full bg-white text-[#050505] py-3.5 text-sm font-medium hover:bg-[#d1d5db] transition-colors disabled:opacity-50">
                  <RefreshCw size={16} /> {retrying ? "Wird gestartet…" : "Zahlung erneut versuchen"}
                </button>
                <button onClick={declineBooking} disabled={cancelling} data-testid="protection-payment-decline-button"
                  className="flex-1 inline-flex items-center justify-center gap-2 rounded-full border border-white/20 text-white py-3.5 text-sm font-medium hover:bg-white/5 transition-colors disabled:opacity-50">
                  <Ban size={16} /> {cancelling ? "Wird storniert…" : "Buchung stornieren"}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </section>
  );
}
